import { fetchAuthSession } from 'aws-amplify/auth';
// Neither `aws-amplify` nor `aws-amplify/auth` re-exports clearCredentials, so
// it has to come from core directly. Deliberately NOT declared in package.json:
// `aws-amplify` pins `@aws-amplify/core` to an exact version, and a second copy
// would hand us a different credentials-provider singleton -- clearing one
// would silently leave the other's cached identity in place.
import { clearCredentials } from '@aws-amplify/core';

// A single-flight cache around fetchAuthSession(), and the reason it exists:
//
// Every userPool-authed client.graphql() call fetches the session internally,
// and with an identity pool configured that session resolves identity-pool
// credentials too. Amplify's credentials provider caches those only in memory
// (empty after every refresh) and does NOT dedupe concurrent requests -- so a
// page that fires several GraphQL calls on mount sends that many independent
// GetId / GetCredentialsForIdentity round-trips to Cognito Identity, which
// throttles them (TooManyRequestsException). api-graphql then relabels ANY
// fetchAuthSession() failure as "NoSignedUser: No current user", even though
// the user-pool tokens are perfectly valid.
//
// Warming the session once -- awaited by WarmedOutlet before any /me page
// mounts -- populates Amplify's credential cache with a single round-trip, so
// the per-call fetches inside client.graphql() become cache hits.
//
// The promise itself is cached (the fetchAllPlaces pattern in places.mjs):
// concurrent callers share one in-flight request, and a failure clears the
// cache so the next caller retries instead of replaying a rejection. No TTL --
// Amplify's own provider handles token/credential expiry; this cache only
// collapses the post-mount stampede.
let warmPromise = null;

// Attach the failure-clears-cache behaviour. The identity check matters: by
// the time an old attempt rejects, a refreshAuthSession() may already have
// published a newer promise, and nulling that one would drop a live recovery.
function published(promise) {
  const wrapped = promise.catch((err) => {
    if (warmPromise === wrapped) warmPromise = null;
    throw err;
  });
  warmPromise = wrapped;
  return wrapped;
}

// Resolves to a session, but treat it as a SIGNAL that credentials are warm,
// not as fresh session data: the resolved object is cached for the life of the
// tab, so its `credentials` and `tokens` go stale. Callers here read only
// `identityId` (stable for the login) or ignore the value entirely. Anything
// needing live credentials must call fetchAuthSession() itself -- Amplify's
// provider owns expiry and will serve or refresh from its own cache.
export function warmAuthSession() {
  if (!warmPromise) {
    published(fetchAuthSession());
  }
  return warmPromise;
}

// The identity-recovery path. Amplify 6.x caches the Cognito Identity Pool
// identityId in TWO places: an in-memory `_primaryIdentityId` on the
// credentials provider, and a persisted guest id under
// `com.amplify.Cognito.<identityPoolId>.identityId`. `loadIdentityId()` reads
// the in-memory copy first and only falls back to the persisted one, and
// `fetchAuthSession({ forceRefresh: true })` clears cached *credentials* but
// neither identity copy. So a stale identity keeps getting replayed into
// GetCredentialsForIdentity, which rejects it against an otherwise valid
// user-pool token:
//   "NotAuthorizedException: Invalid login token. Couldn't verify signed token."
// clearCredentials() is the only API that drops both copies -- it forces
// Amplify to call GetId again and bind a fresh identity to the current login.
//
// The clear-then-refresh is published AS the shared warm promise, so anything
// asking for a session while the wipe is in flight joins the recovery instead
// of racing a freshly-emptied cache. (Residual: a client.graphql() call that
// STARTS inside this window still cold-misses -- its internal fetch doesn't
// consult this module -- but the window only opens when the identity was
// genuinely broken.)
export function refreshAuthSession() {
  return published((async () => {
    try {
      await clearCredentials();
    } catch (_) {
      // Best-effort; if the store can't be cleared the forced refresh below
      // simply behaves like a first attempt.
    }
    return fetchAuthSession({ forceRefresh: true });
  })());
}

// Forget the warmed session. Called on sign-out so a second user in the same
// tab never inherits the first user's session promise.
export function resetAuthSessionWarm() {
  warmPromise = null;
}
